import { API_BASE } from '../lib/apiOrigin';
import PreferredDateOptions from './quote/PreferredDateOptions';
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  X,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Send,
  Upload,
  Trash2,
  Link as LinkIcon,
  Phone,
  Sparkles,
  Camera,
  Settings,
  Home,
  Zap,
  Trees,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { tr, localizeOption } from "../i18n/optionLabels";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  QUOTE_CATEGORIES,
  GAZEBO_SIZES,
  STRUCTURE_MATERIALS,
  ROOF_TYPES,
  SHED_MATERIALS,
  FOUNDATION_TYPES,
  PLATFORM_OPTIONS,
  INSTALL_LOCATIONS,
  GROUND_TYPES,
  COMPLEXITY_LEVELS,
  ACCESS_LEVELS,
  ACCESSORY_OPTIONS,
  ANCHORING_OPTIONS,
  CLEANUP_OPTIONS,
  BOXES_OPTIONS,
  PURCHASED_FROM_OPTIONS,
  DELIVERY_STATUS_OPTIONS,
  EXISTING_SURFACE_OPTIONS,
  SURFACE_TYPE_OPTIONS,
  TIME_WINDOW_OPTIONS,
  ANCHORING_COUNT_OPTIONS,
  OPTIONAL_ADDONS,
  ACKNOWLEDGMENTS,
} from "./quote/quoteConfig";

import CatalogPicker, { catalogSelectionValid, catalogNeedsSize } from "./quote/CatalogPicker";
import EstimatePreview from "./quote/EstimatePreview";
import ConcreteFootings from "./quote/ConcreteFootings";
import QuotePaymentPreference from "./quote/QuotePaymentPreference";
import QuoteOutcome from "./quote/QuoteOutcome";

import { categoryForServiceGroup, initialCatalogBrowse } from './quote/catalogSearch';
import { deliverySelection, deliveryPayload } from './quote/quoteDelivery';
import { normalizeOptionalAddons, toggleOptionalAddon } from './quote/quoteOptionalAddons';

import { quoteIssues, quoteValidationCopy } from "./quote/quoteValidation";

import { createQuotePhotoUploads } from "./quote/quotePhotoUploads";
import ZipLocationFields from "./quote/ZipLocationFields";
import { useZipLocation, ZIP_LOCATION_ERRORS } from "./quote/useZipLocation";

const API = API_BASE;

const initialState = {
  catalog_selection: null,
  category: "",
  product_type: "",
  product_size: "",
  product_size_custom: "",
  product_links: [],
  photos: [],
  backyard_photos: [],
  inspiration_photos: [],
  // Configuration
  structure_material: "",
  roof_type: "",
  shed_material: "",
  foundation_type: "",
  platform_option: "",
  // Purchase / delivery
  structure_brand: "",
  purchased_from: "",
  delivery_status: "",
  estimated_delivery_date: "",
  // Site
  service_address: "",
  install_location: "",
  ground_type: "",
  complexity: "",
  backyard_access: "",
  electrical_access: false,
  // Conditional site work
  needs_platform_built: false,
  existing_surface: "",
  needs_teardown: false,
  needs_relocation: false,
  // Accessories + cleanup
  accessories: [],
  addons: [],            // anchoring
  cleanup_services: [],
  optional_addons: [],   // cardboard/disposal toggles
  concrete_footings_qty: 0,
  footings_bare_soil_confirmed: false,
  preferred_deposit_method: '',
  // Required Yes/No gates
  has_blinds: null,
  blinds_qty: 0,
  needs_anchoring: null,
  anchoring_count: 0,
  has_led: null,
  needs_pallet_removal: null,
  pallets_qty: 0,
  // Surface & delivery
  surface_type: "",
  product_delivered: null,
  // Schedule
  preferred_date: "",
  preferred_dates: [],
  time_window: "",
  // Acknowledgments (all required)
  ack_15ft: false,
  ack_clear_access: false,
  ack_site_prep: false,
  ack_access_conditions: false,
  ack_price_protection: false,
  // Qualification
  already_delivered: false,
  boxes_count: "",
  hoa_required: false,
  hoa_restrictions: "",
  has_manual: false,
  // Contact
  name: "",
  email: "",
  phone: "",
  location: "",
  city: "",
  state: "",
  postal_code: "",
  location_confirmed: false,
  location_lookup_version: "",
  message: "",
  special_instructions: "",
};

const ALL_STEPS = [
  { id: "product", labelKey: "step_label_product", Icon: Sparkles },
  { id: "configure", labelKey: "step_label_configure", Icon: Settings },
  { id: "site", labelKey: "step_label_site", Icon: Home },
  { id: "accessories", labelKey: "step_label_accessories", Icon: Trees },
  { id: "schedule", labelKey: "step_label_schedule", Icon: Zap },
  { id: "qualification", labelKey: "step_label_qualification", Icon: Zap },
  { id: "agreement", labelKey: "step_label_agreement", Icon: Zap },
  { id: "contact", labelKey: "step_label_contact", Icon: Phone },
];

const needsConfigure = (cat) => {
  const c = QUOTE_CATEGORIES.find((x) => x.id === cat);
  return !!(c && (c.hasMaterial || c.hasRoof || c.hasShed));
};

const QuoteFormDialog = ({ open, onOpenChange, initialServiceGroup = '' }) => {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...initialState, category: categoryForServiceGroup(initialServiceGroup) });
  const [catalogBrowse, setCatalogBrowse] = useState(initialCatalogBrowse);
  const [linkInput, setLinkInput] = useState("");
  const [status, setStatus] = useState("idle");
  const [receipt, setReceipt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const fileRef = useRef(null);
  const bodyRef = useRef(null);
  const submitting = useRef(false);
  const [issueTarget, setIssueTarget] = useState(null);
  const photoText = useRef(t.form);
  photoText.current = t.form;
  const photoUploads = useRef(null);
  if (!photoUploads.current) {
    photoUploads.current = createQuotePhotoUploads({
      onChange: (field, photos) => setForm(previous => ({ ...previous, [field]: photos })),
      onError: (key, name) => setErrorMsg(photoText.current[key].replace("{name}", name)),
    });
  }
  useEffect(() => {
    const uploads = photoUploads.current;
    return () => uploads.reset();
  }, []);

  // Compute the visible steps based on category (skip Configure if N/A)
  const STEPS = ALL_STEPS.filter(
    (s) => s.id !== "configure" || (!form.catalog_selection && needsConfigure(form.category))
  );

  const update = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    if (!['gazebo', 'pergola'].includes(next.category) || next.surface_type !== 'dirt') {
      next.concrete_footings_qty = 0;
      next.footings_bare_soil_confirmed = false;
    } else if (patch.surface_type !== undefined && patch.surface_type !== f.surface_type) {
      next.footings_bare_soil_confirmed = false;
    }
    return next;
  });

  const handleClose = () => {
    zipLocation.reset();
    photoUploads.current.reset();
    onOpenChange(false);
    setTimeout(() => {
      photoUploads.current.reset();
      setStep(0);
      setForm({ ...initialState, category: categoryForServiceGroup(initialServiceGroup) });
      setCatalogBrowse(initialCatalogBrowse);
      setLinkInput("");
      setStatus("idle");
      setReceipt(null);
      setErrorMsg("");
    }, 300);
  };

  const next = () => { if (!submitting.current) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => { if (!submitting.current) setStep((s) => Math.max(s - 1, 0)); };

  const onFilesTo = (field, filesArr) => photoUploads.current.add(field, filesArr);
  const onFiles = (filesArr) => onFilesTo("photos", filesArr);
  const removePhotoAt = (field, i) => photoUploads.current.remove(field, i);
  const removePhoto = (i) => removePhotoAt("photos", i);

  const addLink = () => {
    const v = linkInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setErrorMsg(t.form.link_invalid);
      return;
    }
    setErrorMsg("");
    update({ product_links: [...form.product_links, v] });
    setLinkInput("");
  };
  const removeLink = (i) =>
    update({ product_links: form.product_links.filter((_, idx) => idx !== i) });

  const toggle = (field, id) => {
    if (field === "optional_addons") {
      setForm(previous => ({ ...previous, optional_addons: toggleOptionalAddon(previous.optional_addons, id) }));
      return;
    }
    update({
      [field]: form[field].includes(id)
        ? form[field].filter((a) => a !== id)
        : [...form[field], id],
    });
  };

  const stepId = STEPS[step]?.id;
  const lastPanelStep = useRef(null);
  useEffect(() => {
    if (!open) { lastPanelStep.current = null; return; }
    const key = `${stepId}:${status}`;
    if (lastPanelStep.current === key) return;
    lastPanelStep.current = key;
    if (issueTarget || !bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
    bodyRef.current.focus({ preventScroll: true });
  }, [open, stepId, status, issueTarget]);
  const zipLocation = useZipLocation({ form, setForm, active: open && stepId === "site" });

  const issues = quoteIssues(form, STEPS, { selectionValid: catalogSelectionValid, needsSize: catalogNeedsSize });
  const finalStep = step === STEPS.length - 1;
  const visibleIssues = finalStep ? issues : issues.filter(issue => issue.step === stepId);
  const validationText = quoteValidationCopy[lang] || quoteValidationCopy.en;
  const stepValid = () => visibleIssues.length === 0;
  const returnToIssue = (issue) => {
    const index = STEPS.findIndex(item => item.id === issue.step);
    if (index >= 0 && !submitting.current) { setStep(index); setIssueTarget(issue.field); setErrorMsg(""); }
  };

  useEffect(() => {
    if (!issueTarget) return;
    const targets = { product: "catalog-service", size: "quote-input-size", delivered: "quote-input-delivery-status", service_address: "quote-input-service-address", postal_code: "quote-input-postal-code", location: "quote-input-place", location_confirmed: "quote-input-location-confirmed", name: "quote-input-name", phone: "quote-input-phone", email: "quote-input-email", date: "preferred-date-input" };
    const target = (targets[issueTarget] && bodyRef.current?.querySelector(`[data-testid="${targets[issueTarget]}"]`)) ||
      (["postal_code", "location", "location_confirmed"].includes(issueTarget) && bodyRef.current?.querySelector('[data-testid="quote-input-postal-code"]'));
    if (target) { target.focus(); target.scrollIntoView?.({ block: "center", behavior: "smooth" }); }
    else if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setIssueTarget(null);
  }, [issueTarget, step]);

  const submit = async () => {
    if (submitting.current || issues.length) return;
    submitting.current = true;
    setStatus("submitting");
    setErrorMsg("");
    try {
      const service =
        QUOTE_CATEGORIES.find((c) => c.id === form.category)?.label ||
        form.category ||
        "Outdoor Structure";
      // Resolve the final product_size — if the user picked "Custom Size", use
      // whatever they typed in the free-text field so the backend / PDF /
      // dashboard show the real dimensions instead of the placeholder label.
      const resolvedSize =
        form.product_size === "Custom Size" && (form.product_size_custom || "").trim()
          ? form.product_size_custom.trim()
          : form.product_size;
      const response = await axios.post(`${API}/quotes`, {
        ...form,
        ...deliveryPayload(form),
        optional_addons: normalizeOptionalAddons(form.optional_addons),
        // Preserve the legacy API field from the one canonical installation date.
        delivery_date: form.preferred_date,
        product_size: resolvedSize,
        service,
        language: lang,
      });
      setReceipt(response.data);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      if (ZIP_LOCATION_ERRORS.includes(detail?.code)) {
        zipLocation.rejectLocation(detail.code);
        setStep(STEPS.findIndex(item => item.id === "site"));
        setIssueTarget("postal_code");
        return;
      }
      let msg = t.form.error;
      if (detail) {
        msg = typeof detail === "string" ? detail : t.form.validation_error;
      }
      setErrorMsg(msg);
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : handleClose()}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-zinc-950 border border-white/10 rounded-none w-[calc(100vw-1rem)] sm:w-auto max-w-3xl p-0 gap-0 overflow-hidden max-h-[94vh] sm:max-h-[92vh] flex flex-col"
      >
        <DialogTitle className="sr-only">Smart Quote Request</DialogTitle>
        <DialogDescription className="sr-only">
          Multi-step intelligent quote form with photo upload, product links,
          configuration, install site details and add-on services.
        </DialogDescription>

        <button
          data-testid="quote-dialog-close"
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 text-zinc-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* HEADER */}
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 px-5 sm:px-7 py-5 sm:py-6 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80 mb-2">
            {status === "success"
              ? t.form.done
              : t.form.step_indicator
                  .replace("{n}", step + 1)
                  .replace("{total}", STEPS.length)}
          </div>
          <h3 className="font-display font-black text-white text-xl sm:text-2xl md:text-3xl leading-tight">
            {status === "success" ? t.form.success : t.form.title}
          </h3>
          {status !== "success" && (
            <p className="text-white/85 text-xs sm:text-sm mt-1.5">
              {t.form[STEPS[step].labelKey]} · {t.form.response_promise}
            </p>
          )}
          {status !== "success" && (
            <div className="mt-4 sm:mt-5 flex gap-1.5" data-testid="quote-progress">
              {STEPS.map((s, i) => (
                <div
                  key={s.id}
                  className={`h-1 flex-1 transition-all duration-500 ${
                    i <= step ? "bg-white" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* BODY */}
        <div ref={bodyRef} data-testid="quote-step-body" data-step={stepId} tabIndex={-1} aria-label={t.form[STEPS[step]?.labelKey]} className="overflow-y-auto px-5 sm:px-7 py-6 sm:py-7 flex-1 outline-none">
          {status === "success" ? (
            <SuccessView onClose={handleClose} receipt={receipt} />
          ) : (
            <>
              {stepId === "product" && (
                <StepProduct
                  catalogBrowse={catalogBrowse}
                  setCatalogBrowse={setCatalogBrowse}
                  form={form}
                  update={update}
                  onFiles={onFiles}
                  removePhoto={removePhoto}
                  linkInput={linkInput}
                  setLinkInput={setLinkInput}
                  addLink={addLink}
                  removeLink={removeLink}
                  fileRef={fileRef}
                  t={t}
                />
              )}
              {stepId === "configure" && (
                <StepConfigure form={form} update={update} t={t} />
              )}
              {stepId === "site" && (
                <StepSite
                  form={form}
                  update={update}
                  zipLocation={zipLocation}
                  onFilesTo={onFilesTo}
                  removePhotoAt={removePhotoAt}
                  t={t}
                />
              )}
              {stepId === "accessories" && (
                <StepAccessories form={form} update={update} toggle={toggle} t={t} />
              )}
              {stepId === "schedule" && (
                <StepSchedule form={form} update={update} t={t} />
              )}
              {stepId === "qualification" && (
                <StepQualification form={form} update={update} t={t} />
              )}
              {stepId === "agreement" && (
                <StepAgreement form={form} update={update} t={t} />
              )}
              {stepId === "contact" && (
                <StepContact form={form} update={update} t={t} />
              )}

              {visibleIssues.length > 0 && (
                <section id="quote-validation-summary" data-testid="quote-validation-summary" role="status" aria-live="polite" className="mt-5 rounded-lg border border-amber-400/40 bg-amber-500/10 p-4 text-sm">
                  <p className="font-semibold text-amber-200">{finalStep ? validationText.final : validationText.title}</p>
                  <ul className="mt-2 space-y-2">
                    {visibleIssues.map(issue => <li key={`${issue.step}-${issue.field}`}>
                      <button type="button" data-testid={`quote-fix-${issue.field}`} onClick={() => returnToIssue(issue)} disabled={status === "submitting"} className="text-left text-amber-200 underline underline-offset-4">
                        {t.form[STEPS.find(item => item.id === issue.step)?.labelKey]}: {validationText[issue.field]}
                      </button>
                    </li>)}
                  </ul>
                </section>
              )}

              {errorMsg && (
                <div
                  data-testid="quote-error"
                  className="mt-6 flex items-start gap-3 bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER NAV */}
        {status !== "success" && (
          <div className="border-t border-white/10 bg-zinc-950 px-5 sm:px-7 py-3.5 sm:py-4 flex items-center justify-between gap-3 shrink-0 safe-pb">
            <button
              data-testid="quote-back-button"
              onClick={back}
              disabled={step === 0 || status === "submitting"}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-display font-semibold uppercase tracking-[0.15em] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft size={14} />
              {t.form.back}
            </button>
            <div className="flex items-center gap-3">
              <a
                href="tel:+15105079318"
                className="hidden sm:inline-flex items-center gap-2 text-zinc-400 hover:text-sky-400 text-xs"
              >
                <Phone size={12} /> (510) 507-9318
              </a>
              {step < STEPS.length - 1 ? (
                <button
                  data-testid="quote-next-button"
                  onClick={next}
                  aria-describedby={visibleIssues.length ? "quote-validation-summary" : undefined}
                  disabled={!stepValid()}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.form.continue}
                  <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  data-testid="quote-submit-button"
                  onClick={submit}
                  aria-describedby={visibleIssues.length ? "quote-validation-summary" : undefined}
                  disabled={!stepValid() || status === "submitting"}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? t.form.submitting : t.form.submit}
                  <Send size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ========== Shared bits ==========
const SectionTitle = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={14} className="text-sky-400" />
    <span className="eyebrow !text-[10px]">{label}</span>
  </div>
);

const Label = ({ children }) => (
  <label className="block eyebrow !text-zinc-500 !text-[10px] mb-2">
    {children}
  </label>
);

const TextInput = ({ value, onChange, placeholder, type = "text", testId, required, min }) => (
  <input
    data-testid={testId}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    required={required}
    min={min}
    className="w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 outline-none transition-colors placeholder:text-zinc-600"
  />
);

const Select = ({ value, onChange, options, placeholder = "Select…", testId }) => {
  const { lang } = useLang();
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 outline-none appearance-none"
    >
      <option value="" className="bg-zinc-900">{placeholder}</option>
      {options.map((o) => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value} className="bg-zinc-900">{tr(typeof o === "string" ? o : o.label, lang)}</option>
      ))}
    </select>
  );
};

const Toggle = ({ checked, onChange, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`flex items-center justify-between w-full text-left border px-4 py-3.5 transition-all ${
      checked ? "border-sky-400 bg-sky-500/5" : "border-white/10 hover:border-white/30"
    }`}
  >
    <span className="text-white text-sm font-medium">{label}</span>
    <span className={`relative h-5 w-9 transition-colors ${checked ? "bg-sky-500" : "bg-zinc-800"}`}>
      <span className={`absolute top-0.5 h-4 w-4 bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </span>
  </button>
);

const PillGrid = ({ options, value, onChange, testIdPrefix, columns = 2 }) => {
  const { lang } = useLang();
  return (
    <div className={`grid grid-cols-${columns} sm:grid-cols-${columns + 1} gap-2`}>
      {options.map((opt) => {
        const loc = localizeOption(opt, lang);
        const active = value === loc.id;
        return (
          <button
            type="button"
            key={loc.id}
            data-testid={`${testIdPrefix}-${loc.id}`}
            onClick={() => onChange(loc.id)}
            className={`text-left p-3.5 border transition-all ${
              active ? "border-sky-400 bg-sky-500/10" : "border-white/10 hover:border-white/30"
            }`}
          >
            <div className="font-display font-bold text-white text-sm leading-tight">{loc.label}</div>
            {loc.desc && <div className="text-zinc-400 text-xs mt-0.5">{loc.desc}</div>}
          </button>
        );
      })}
    </div>
  );
};

const CheckCard = ({ active, onClick, label, price, badge, emoji, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`w-full text-left p-4 border transition-all flex items-start gap-3 ${
      active ? "border-sky-400 bg-sky-500/5" : "border-white/10 hover:border-white/30 bg-zinc-950"
    }`}
  >
    <div className={`shrink-0 mt-0.5 h-5 w-5 border-2 flex items-center justify-center transition-colors ${
      active ? "bg-sky-500 border-sky-500" : "border-white/30"
    }`}>
      {active && <CheckCircle2 size={14} className="text-white" />}
    </div>
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2">
        {emoji && <span className="text-base">{emoji}</span>}
        <span className="font-display font-bold text-white text-sm">{label}</span>
        {badge && (
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${
            badge === "Premium" ? "bg-[#d4af37] text-black" : "bg-sky-500 text-white"
          }`}>{badge}</span>
        )}
      </div>
    </div>
    {price && <div className="shrink-0 font-display font-bold text-sky-400 text-sm">{price}</div>}
  </button>
);

// ========== STEP: PRODUCT ==========
const StepProduct = ({ catalogBrowse, setCatalogBrowse, form, update, onFiles, removePhoto, linkInput, setLinkInput, addLink, removeLink, fileRef, t }) => (
  <div className="space-y-7">
    <CatalogPicker form={form} update={update} browse={catalogBrowse} onBrowseChange={setCatalogBrowse} />

    {!form.catalog_selection?.productId && <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <Label>{t.form.brand_model}</Label>
        <TextInput value={form.product_type} onChange={(v) => update({ product_type: v })} placeholder={t.form.brand_model_ph} testId="quote-input-product-type" />
      </div>
      <div>
        <Label>{t.form.structure_brand}</Label>
        <TextInput value={form.structure_brand} onChange={(v) => update({ structure_brand: v })} placeholder={t.form.structure_brand_ph} testId="quote-input-structure-brand" />
      </div>
      <div>
        <Label>{t.form.purchased_from}</Label>
        <Select value={form.purchased_from} onChange={(v) => update({ purchased_from: v })} options={PURCHASED_FROM_OPTIONS} testId="quote-input-purchased-from" placeholder={t.form.purchased_from_ph} />
      </div>
      <div>
        <Label>{t.form.size} <span className="text-red-400">*</span></Label>
        <Select value={form.product_size} onChange={(v) => update({ product_size: v })} options={GAZEBO_SIZES} testId="quote-input-size" placeholder={t.form.size_ph} />
        {form.product_size === "Custom Size" && (
          <div className="mt-3">
            <TextInput
              value={form.product_size_custom}
              onChange={(v) => update({ product_size_custom: v })}
              placeholder={t.form.size_custom_ph || "Enter size (e.g. 15×22)"}
              testId="quote-input-size-custom"
            />
          </div>
        )}
      </div>
    </div>}

    <div>
      <SectionTitle icon={LinkIcon} label={t.form.product_links} />
      <div className="flex gap-2 mb-3">
        <input
          data-testid="quote-link-input"
          value={linkInput}
          onChange={(e) => setLinkInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())}
          placeholder={t.form.product_links_ph}
          className="flex-1 bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-2.5 outline-none text-sm"
        />
        <button type="button" onClick={addLink} data-testid="quote-link-add" className="px-4 bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold uppercase tracking-wider transition-colors">+</button>
      </div>
      {form.product_links.length > 0 && (
        <ul className="space-y-2">
          {form.product_links.map((l, i) => (
            <li key={l} data-testid={`quote-link-${i}`} className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 text-xs">
              <a href={l} target="_blank" rel="noreferrer" className="text-sky-400 truncate hover:underline">{l}</a>
              <button type="button" onClick={() => removeLink(i)} className="text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>

    <div>
      <SectionTitle icon={Camera} label={t.form.photos} />
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-white/15 hover:border-sky-400 transition-colors p-6 cursor-pointer text-center"
        data-testid="quote-photo-dropzone"
      >
        <Upload size={20} className="mx-auto text-sky-400 mb-2" />
        <div className="text-white text-sm font-medium">{t.form.drop_or_click}</div>
        <div className="text-zinc-500 text-xs mt-1">{t.form.photo_hint}</div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid="quote-photo-input" />
      {form.photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
          {form.photos.map((p, i) => (
            <div key={i} className="relative aspect-square group">
              <img src={p} alt={`upload-${i}`} className="absolute inset-0 w-full h-full object-cover border border-white/10" />
              <button type="button" onClick={() => removePhoto(i)} data-testid={`quote-photo-remove-${i}`} className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><X size={10} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ========== STEP: CONFIGURE ==========
const StepConfigure = ({ form, update, t }) => {
  const cat = QUOTE_CATEGORIES.find((c) => c.id === form.category);
  if (!cat) return null;

  return (
    <div className="space-y-7">
      <p className="text-zinc-400 text-sm">
        {t.form.configure_intro.replace("{category}", cat.label.toLowerCase())}
      </p>

      {cat.hasMaterial && (
        <div>
          <Label>{t.form.structure_material}</Label>
          <PillGrid
            options={STRUCTURE_MATERIALS}
            value={form.structure_material}
            onChange={(v) => update({ structure_material: v })}
            testIdPrefix="material"
            columns={2}
          />
        </div>
      )}

      {cat.hasRoof && (
        <div>
          <Label>{t.form.roof_type}</Label>
          <PillGrid
            options={ROOF_TYPES}
            value={form.roof_type}
            onChange={(v) => update({ roof_type: v })}
            testIdPrefix="roof"
            columns={2}
          />
        </div>
      )}

      {cat.hasShed && (
        <>
          <div>
            <Label>{t.form.shed_material}</Label>
            <PillGrid
              options={SHED_MATERIALS}
              value={form.shed_material}
              onChange={(v) => update({ shed_material: v })}
              testIdPrefix="shed-material"
              columns={2}
            />
          </div>

          <div>
            <Label>{t.form.foundation_type}</Label>
            <PillGrid
              options={FOUNDATION_TYPES}
              value={form.foundation_type}
              onChange={(v) => update({ foundation_type: v })}
              testIdPrefix="foundation"
              columns={2}
            />
          </div>

          {(form.foundation_type === "Need Wood Platform" ||
            form.foundation_type === "Need Concrete Foundation") && (
            <div>
              <Label>{t.form.platform_option}</Label>
              <PillGrid
                options={PLATFORM_OPTIONS}
                value={form.platform_option}
                onChange={(v) => update({ platform_option: v })}
                testIdPrefix="platform"
                columns={1}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ========== STEP: SITE ==========
const StepSite = ({ form, update, zipLocation, onFilesTo, removePhotoAt, t }) => {
  const { lang } = useLang();
  const backyardRef = useRef(null);
  const inspirationRef = useRef(null);
  return (
    <div className="space-y-7">
      <ZipLocationFields form={form} lookup={zipLocation} />
      <label className="block text-sm font-semibold text-white">{lang === "pt" ? "Endereço da instalação: rua, número e complemento" : lang === "es" ? "Dirección de instalación: calle, número y unidad" : "Installation address: street, number and unit"} <span className="text-red-400">*</span><input data-testid="quote-input-service-address" name="service_address" type="text" autoComplete="street-address" required maxLength={300} value={form.service_address || ""} onChange={e=>update({service_address:e.target.value})} className="mt-2 w-full border border-white/20 bg-zinc-950 p-3 rounded text-white" /></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label>{t.form.install_location}</Label>
          <Select value={form.install_location} onChange={(v) => update({ install_location: v })} options={INSTALL_LOCATIONS} testId="quote-input-install-location" placeholder={t.form.install_location_ph} />
        </div>
        <div>
          <Label>{t.form.ground_type}</Label>
          <Select value={form.ground_type} onChange={(v) => update({ ground_type: v })} options={GROUND_TYPES} testId="quote-input-ground" placeholder={t.form.ground_type_ph} />
        </div>
      </div>

      <div>
        <Label>{t.form.surface_type} <span className="text-red-400">*</span></Label>
        <PillGrid
          options={SURFACE_TYPE_OPTIONS}
          value={form.surface_type}
          onChange={(v) => update({ surface_type: v })}
          testIdPrefix="surface-type"
          columns={2}
        />
      </div>

      <div className="border border-white/10 bg-zinc-950/40 p-4 space-y-4" data-testid="quote-delivery-details">
        <label className="block text-white font-semibold text-sm">
          {t.form.delivery_status} <span className="text-red-400">*</span>
          <select
            data-testid="quote-input-delivery-status"
            value={form.delivery_status}
            onChange={e => update(deliverySelection(e.target.value))}
            required
            className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 outline-none"
          >
            <option value="" className="bg-zinc-900">{t.form.boxes_received_ph}</option>
            <option value="delivered" className="bg-zinc-900">{t.form.delivery_delivered}</option>
            <option value="scheduled" className="bg-zinc-900">{t.form.delivery_scheduled}</option>
            <option value="not-yet" className="bg-zinc-900">{t.form.delivery_not_yet}</option>
          </select>
        </label>
        {form.delivery_status === 'scheduled' && <label className="block text-zinc-300 text-sm">
          {t.form.estimated_delivery_date}
          <TextInput type="date" value={form.estimated_delivery_date} onChange={v => update({ estimated_delivery_date: v })} testId="quote-input-eta" />
        </label>}
        {form.delivery_status === 'delivered' && <label className="block text-zinc-300 text-sm">
          {t.form.boxes_received}
          <Select value={form.boxes_count} onChange={v => update({ boxes_count: v })} options={BOXES_OPTIONS.filter(option => option !== 'Not delivered yet')} testId="quote-q-boxes" placeholder={t.form.boxes_received_ph} />
        </label>}
      </div>

      <div>
        <Label>{t.form.existing_surface}</Label>
        <PillGrid
          options={EXISTING_SURFACE_OPTIONS}
          value={form.existing_surface}
          onChange={(v) => update({ existing_surface: v })}
          testIdPrefix="existing-surface"
          columns={2}
        />
      </div>

      <div>
        <Label>{t.form.backyard_access}</Label>
        <PillGrid
          options={ACCESS_LEVELS}
          value={form.backyard_access}
          onChange={(v) => update({ backyard_access: v })}
          testIdPrefix="access"
          columns={1}
        />
      </div>

      <div>
        <Label>{t.form.assembly_complexity}</Label>
        <PillGrid
          options={COMPLEXITY_LEVELS}
          value={form.complexity}
          onChange={(v) => update({ complexity: v })}
          testIdPrefix="quote-complexity"
          columns={1}
        />
      </div>

      <div>
        <Label>{t.form.site_work_optional}</Label>
        <div className="grid grid-cols-1 gap-2">
          <Toggle
            checked={form.needs_platform_built}
            onChange={(v) => update({ needs_platform_built: v })}
            label={t.form.build_platform}
            testId="quote-q-platform-built"
          />
          <Toggle
            checked={form.needs_teardown}
            onChange={(v) => update({ needs_teardown: v })}
            label={t.form.teardown_old}
            testId="quote-q-teardown"
          />
          <Toggle
            checked={form.needs_relocation}
            onChange={(v) => update({ needs_relocation: v })}
            label={t.form.relocate_existing}
            testId="quote-q-relocation"
          />
          <Toggle
            checked={form.electrical_access}
            onChange={(v) => update({ electrical_access: v })}
            label={t.form.electrical_access}
            testId="quote-q-electrical"
          />
        </div>
      </div>

      <PhotoUploader
        label={t.form.backyard_photos}
        hint={t.form.backyard_photos_hint}
        photos={form.backyard_photos}
        onFiles={(files) => onFilesTo("backyard_photos", files)}
        onRemove={(i) => removePhotoAt("backyard_photos", i)}
        inputRef={backyardRef}
        testPrefix="backyard"
        dropHint={t.form.drop_or_click}
        sizeHint={t.form.photo_hint}
      />

      <PhotoUploader
        label={t.form.inspiration_photos}
        hint={t.form.inspiration_photos_hint}
        photos={form.inspiration_photos}
        onFiles={(files) => onFilesTo("inspiration_photos", files)}
        onRemove={(i) => removePhotoAt("inspiration_photos", i)}
        inputRef={inspirationRef}
        testPrefix="inspiration"
        dropHint={t.form.drop_or_click}
        sizeHint={t.form.photo_hint}
      />
    </div>
  );
};

// Reusable photo uploader block used inside Site step
const PhotoUploader = ({ label, hint, photos, onFiles, onRemove, inputRef, testPrefix, dropHint, sizeHint }) => (
  <div>
    <Label>{label}</Label>
    {hint && <p className="text-zinc-500 text-xs -mt-1 mb-3">{hint}</p>}
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
      className="border-2 border-dashed border-white/15 hover:border-sky-400 transition-colors p-5 cursor-pointer text-center"
      data-testid={`${testPrefix}-photo-dropzone`}
    >
      <Upload size={18} className="mx-auto text-sky-400 mb-2" />
      <div className="text-white text-sm font-medium">{dropHint || "Drop or click to upload"}</div>
      <div className="text-zinc-500 text-xs mt-1">{sizeHint || "JPG / PNG / WEBP · 6 per section · 1MB each · 6MB total across all sections"}</div>
    </div>
    <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid={`${testPrefix}-photo-input`} />
    {photos.length > 0 && (
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square group">
            <img src={p} alt={`${testPrefix}-${i}`} className="absolute inset-0 w-full h-full object-cover border border-white/10" />
            <button type="button" onClick={() => onRemove(i)} data-testid={`${testPrefix}-photo-remove-${i}`} className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><X size={10} /></button>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ========== STEP: ACCESSORIES (required Yes/No gates + optional toggles) ==========
// Prices shown here come only from the server estimate (EstimatePreview).
const StepAccessories = ({ form, update, toggle, t }) => {
  const optionalAddons = normalizeOptionalAddons(form.optional_addons);
  const OPT_ADDONS_LOCAL = [
    { id: "cardboard-removal", label: t.form.addon_cardboard, price: "+$45" },
    { id: "disposal-service", label: t.form.addon_disposal, price: "+$120" },
  ];
  return (
    <div className="space-y-8">
      <p className="text-zinc-400 text-sm">{t.form.addons_intro}</p>

      <EstimatePreview form={form} />

      {/* BLINDS */}
      <YesNoBlock
        question={t.form.blinds_q}
        value={form.has_blinds}
        onChange={(v) =>
          update({
            has_blinds: v,
            blinds_qty: v === true ? Math.max(1, form.blinds_qty || 1) : 0,
          })
        }
        testIdPrefix="blinds"
        yesLabel={t.form.yes}
        noLabel={t.form.no}
      >
        {form.has_blinds === true && (
          <div className="mt-4">
            <Label>{t.form.blinds_count_q}</Label>
            <NumberStepper
              value={form.blinds_qty}
              onChange={(v) => update({ blinds_qty: v })}
              min={1}
              max={20}
              testId="blinds-qty"
            />
          </div>
        )}
      </YesNoBlock>

      {/* ANCHORING */}
      <YesNoBlock
        question={t.form.anchoring_q}
        value={form.needs_anchoring}
        onChange={(v) => update({ needs_anchoring: v, ...(v === false && { anchoring_count: 0 }) })}
        testIdPrefix="anchoring"
        yesLabel={t.form.yes}
        noLabel={t.form.no}
      >
        {form.needs_anchoring === true && (
          <div className="mt-4">
            <Label>{t.form.anchoring_count_q}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: "4", label: t.form.anchoring_4_label, price: "$120" },
                { id: "6", label: t.form.anchoring_6_label, price: "$180" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`anchoring-count-${opt.id}`}
                  onClick={() => update({ anchoring_count: parseInt(opt.id, 10) })}
                  className={`text-left px-4 py-3 border transition-colors ${
                    form.anchoring_count === parseInt(opt.id, 10)
                      ? "border-sky-400 bg-sky-500/10"
                      : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <div className="font-display text-white font-bold text-base">{opt.label}</div>
                  <div className="text-sky-400 text-sm font-semibold mt-0.5">{opt.price}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </YesNoBlock>

      {/* LED */}
      <YesNoBlock
        question={t.form.led_q}
        value={form.has_led}
        onChange={(v) => update({ has_led: v })}
        testIdPrefix="led"
        yesLabel={t.form.yes}
        noLabel={t.form.no}
      />

      {/* PALLET REMOVAL */}
      <YesNoBlock
        question={t.form.pallet_q}
        value={form.needs_pallet_removal}
        onChange={(v) =>
          update({
            needs_pallet_removal: v,
            pallets_qty: v === true ? Math.max(1, form.pallets_qty || 1) : 0,
          })
        }
        testIdPrefix="pallet"
        yesLabel={t.form.yes}
        noLabel={t.form.no}
      >
        {form.needs_pallet_removal === true && (
          <div className="mt-4">
            <Label>{t.form.pallet_count_q}</Label>
            <NumberStepper
              value={form.pallets_qty}
              onChange={(v) => update({ pallets_qty: v })}
              min={1}
              max={20}
              testId="pallets-qty"
            />
            {form.pallets_qty > 0 && (
              <div className="mt-2 text-xs text-zinc-400">
                {t.form.estimated_cost}{" "}
                <span className="text-sky-400 font-semibold">${form.pallets_qty * 45}</span>
              </div>
            )}
          </div>
        )}
      </YesNoBlock>

      {/* OPTIONAL INSTALL ADD-ONS */}
      <ConcreteFootings form={form} update={update} />
      <div>
        <SectionTitle icon={Trees} label={t.form.optional_addons} />
        <p className="text-zinc-500 text-xs mb-3">{t.form.optional_addons_hint}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OPT_ADDONS_LOCAL.map((a) => (
            <CheckCard
              key={a.id}
              testId={`optional-addon-${a.id}`}
              active={optionalAddons.includes(a.id)}
              onClick={() => toggle("optional_addons", a.id)}
              label={a.label}
              price={a.price}
            />
          ))}
        </div>
        <p className="mt-3 text-xs text-zinc-400" data-testid="optional-addons-bundle-note">
          {t.form.optional_addons_bundle_note}
        </p>
      </div>
    </div>
  );
};

// Reusable Yes/No block with optional conditional children
const YesNoBlock = ({ question, value, onChange, testIdPrefix, children, yesLabel = "YES", noLabel = "NO" }) => (
  <div className="border border-white/10 bg-zinc-950/40 p-4 sm:p-5">
    <div className="text-white font-semibold text-sm sm:text-base mb-3">{question}</div>
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        data-testid={`${testIdPrefix}-yes`}
        onClick={() => onChange(true)}
        className={`py-3 border transition-colors font-display font-semibold text-sm tracking-wide ${
          value === true
            ? "border-sky-400 bg-sky-500/15 text-white"
            : "border-white/10 text-zinc-300 hover:border-white/25"
        }`}
      >
        {yesLabel}
      </button>
      <button
        type="button"
        data-testid={`${testIdPrefix}-no`}
        onClick={() => onChange(false)}
        className={`py-3 border transition-colors font-display font-semibold text-sm tracking-wide ${
          value === false
            ? "border-sky-400 bg-sky-500/15 text-white"
            : "border-white/10 text-zinc-300 hover:border-white/25"
        }`}
      >
        {noLabel}
      </button>
    </div>
    {children}
  </div>
);

// Numeric stepper -/+ for quantities
const NumberStepper = ({ value, onChange, min = 0, max = 99, testId }) => (
  <div className="flex items-center gap-3" data-testid={testId}>
    <button
      type="button"
      onClick={() => onChange(Math.max(min, (value || 0) - 1))}
      data-testid={`${testId}-decrement`}
      className="h-10 w-10 border border-white/15 text-white hover:border-sky-400 hover:text-sky-400 transition-colors"
    >
      −
    </button>
    <input
      type="number"
      min={min}
      max={max}
      value={value || 0}
      onChange={(e) => {
        const n = parseInt(e.target.value || "0", 10);
        onChange(Math.min(max, Math.max(min, isNaN(n) ? 0 : n)));
      }}
      data-testid={`${testId}-input`}
      className="w-20 text-center bg-transparent border-b border-white/15 text-white py-2 outline-none focus:border-sky-400"
    />
    <button
      type="button"
      onClick={() => onChange(Math.min(max, (value || 0) + 1))}
      data-testid={`${testId}-increment`}
      className="h-10 w-10 border border-white/15 text-white hover:border-sky-400 hover:text-sky-400 transition-colors"
    >
      +
    </button>
  </div>
);

// ========== STEP: SCHEDULE (preferred date + time window) ==========
const StepSchedule = ({ form, update, t }) => {
  const {lang} = useLang();
  const today = new Date().toISOString().split("T")[0];
  // Localized time window options
  const TIME_OPTS = [
    { id: "morning", label: t.form.time_morning, desc: t.form.time_morning_desc },
    { id: "afternoon", label: t.form.time_afternoon, desc: t.form.time_afternoon_desc },
    { id: "evening", label: t.form.time_evening, desc: t.form.time_evening_desc },
    { id: "flexible", label: t.form.time_flexible, desc: t.form.time_flexible_desc },
  ];
  return (
    <div className="space-y-7">
      <p className="text-zinc-400 text-sm">{t.form.schedule_intro}</p>
      <div>
        <Label>{t.form.preferred_date} <span className="text-red-400">*</span></Label>
        <TextInput
          type="date"
          value={form.preferred_date}
          onChange={(v) => update({ preferred_date: v, preferred_dates: [v,...(form.preferred_dates||[]).slice(1)] })}
          testId="preferred-date-input"
          min={today}
        />
      </div>
      <PreferredDateOptions primary={form.preferred_date} values={form.preferred_dates||[]} onChange={values=>update({preferred_dates:values})} min={today} lang={lang}/>
      <div>
        <Label>{t.form.time_window} <span className="text-red-400">*</span></Label>
        <PillGrid
          options={TIME_OPTS}
          value={form.time_window}
          onChange={(v) => update({ time_window: v })}
          testIdPrefix="time-window"
          columns={2}
        />
      </div>
    </div>
  );
};

// ========== STEP: AGREEMENT (5 required acknowledgments) ==========
const StepAgreement = ({ form, update, t }) => {
  // Map ack id → translated label/text from current language
  const TX_ACKS = [
    { id: "ack_15ft", label: t.form.ack_15ft_label, text: t.form.ack_15ft_text },
    { id: "ack_clear_access", label: t.form.ack_clear_access_label, text: t.form.ack_clear_access_text },
    { id: "ack_site_prep", label: t.form.ack_site_prep_label, text: t.form.ack_site_prep_text },
    { id: "ack_access_conditions", label: t.form.ack_access_conditions_label, text: t.form.ack_access_conditions_text },
    { id: "ack_price_protection", label: t.form.ack_price_protection_label, text: t.form.ack_price_protection_text },
  ];
  return (
    <div className="space-y-5">
      <p className="text-zinc-400 text-sm">{t.form.agreement_intro}</p>
      {TX_ACKS.map((ack) => {
        const checked = !!form[ack.id];
        return (
          <button
            type="button"
            key={ack.id}
            data-testid={`ack-${ack.id}`}
            onClick={() => update({ [ack.id]: !checked })}
            className={`w-full text-left border p-4 transition-colors ${
              checked
                ? "border-sky-400 bg-sky-500/10"
                : "border-white/10 bg-zinc-950/40 hover:border-white/25"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 h-5 w-5 border flex items-center justify-center shrink-0 ${
                  checked
                    ? "border-sky-400 bg-sky-400 text-zinc-900"
                    : "border-white/30"
                }`}
              >
                {checked && <span className="text-xs font-black">✓</span>}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-white text-sm mb-1">
                  {ack.label}
                </div>
                <p className="text-zinc-400 text-xs leading-relaxed">{ack.text}</p>
              </div>
            </div>
          </button>
        );
      })}
      <div className="border-l-2 border-amber-400 bg-amber-500/5 px-3 py-2 text-amber-200 text-xs leading-relaxed">
        <strong>{t.form.price_protection_label}</strong> {t.form.price_protection_notice}
      </div>
    </div>
  );
};

// ========== STEP: QUALIFICATION ==========
const StepQualification = ({ form, update, t }) => {
  // Localized HOA options
  const HOA_OPTS = [
    { id: "no", label: t.form.hoa_no, desc: t.form.hoa_no_desc },
    { id: "yes", label: t.form.hoa_yes, desc: t.form.hoa_yes_desc },
    { id: "unknown", label: t.form.hoa_unknown, desc: t.form.hoa_unknown_desc },
  ];
  return (
    <div className="space-y-6">
      <p className="text-zinc-400 text-sm">{t.form.qual_intro}</p>

      <div>
        <Label>{t.form.hoa_q}</Label>
        <PillGrid
          options={HOA_OPTS}
          value={form.hoa_restrictions}
          onChange={(v) => update({ hoa_restrictions: v, hoa_required: v === "yes" })}
          testIdPrefix="hoa"
          columns={1}
        />
      </div>

      <Toggle
        checked={form.has_manual}
        onChange={(v) => update({ has_manual: v })}
        label={t.form.has_manual}
        testId="quote-q-manual"
      />

      <div>
        <Label>{t.form.special_instructions}</Label>
        <textarea
          data-testid="quote-input-special"
          rows={3}
          value={form.special_instructions}
          onChange={(e) => update({ special_instructions: e.target.value })}
          placeholder={t.form.special_instructions_ph}
          className="w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 outline-none resize-none placeholder:text-zinc-600"
        />
      </div>
    </div>
  );
};

// ========== STEP: CONTACT ==========
const StepContact = ({ form, update, t }) => (
  <div className="space-y-5">
    <p className="text-zinc-400 text-sm">{t.form.contact_intro}</p>
    <QuotePaymentPreference form={form} update={update} />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <Label>{t.form.full_name_required}</Label>
        <TextInput value={form.name} onChange={(v) => update({ name: v })} placeholder={t.form.name_ph} required testId="quote-input-name" />
      </div>
      <div>
        <Label>{t.form.phone_required}</Label>
        <TextInput value={form.phone} onChange={(v) => update({ phone: v })} placeholder={t.form.phone_ph} required testId="quote-input-phone" />
      </div>
      <div className="sm:col-span-2">
        <Label>{t.form.email_required}</Label>
        <TextInput type="email" value={form.email} onChange={(v) => update({ email: v })} placeholder={t.form.email_ph} required testId="quote-input-email" />
      </div>
    </div>
    <div>
      <Label>{t.form.additional_notes}</Label>
      <textarea
        data-testid="quote-input-message"
        rows={3}
        value={form.message}
        onChange={(e) => update({ message: e.target.value })}
        placeholder={t.form.additional_notes_ph}
        className="w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 outline-none resize-none placeholder:text-zinc-600"
      />
    </div>
    <p className="text-zinc-500 text-xs">
      {t.form.consent_text}
    </p>
  </div>
);

// ========== SUCCESS ==========
export const SuccessView = ({ onClose, receipt }) => {
  const { t } = useLang();
  return (
    <div data-testid="quote-success" className="py-10 text-center space-y-6">
      <div className="mx-auto h-16 w-16 flex items-center justify-center bg-sky-500/10 border border-sky-400 text-sky-400">
        <CheckCircle2 size={32} />
      </div>
      <p className="text-white font-display text-2xl max-w-md mx-auto">{t.form.success}</p>
      <p className="text-zinc-400 text-sm max-w-md mx-auto">
        {t.form.success_desc}
      </p>
      <p data-testid="confirmation-email-status" className="text-zinc-400 text-sm max-w-md mx-auto">
        {receipt?.notification_status?.customer === "provider_accepted"
          ? t.form.confirmation_email_accepted
          : t.form.confirmation_email_unconfirmed}
      </p>
      <QuoteOutcome receipt={receipt} />
      <div className="flex flex-wrap justify-center gap-3 pt-4">
        <a href="tel:+15105079318" className="btn-secondary">
          <Phone size={14} />
          (510) 507-9318
        </a>
        <button onClick={onClose} data-testid="quote-success-close" className="btn-primary">{t.form.close}</button>
      </div>
    </div>
  );
};

export default QuoteFormDialog;
